import React, { useState } from 'react'
import { aiService } from '@templatean/shared/services'
import { LoadingSpinner } from '@templatean/shared/components'
import { PowerPointService } from '@templatean/shared/services/office.service'
import { useOffice } from '@templatean/shared/hooks'

interface SlideOutline {
  slideNumber: number
  title: string
  bulletPoints: string[]
  slideType: string
  notes?: string
}

const PresentationBuilder: React.FC = () => {
  const { service } = useOffice()
  const [topic, setTopic] = useState('')
  const [slideCount, setSlideCount] = useState(10)
  const [audience, setAudience] = useState('general')
  const [tone, setTone] = useState('professional')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [outline, setOutline] = useState<SlideOutline[]>([])
  const [selectedSlides, setSelectedSlides] = useState<Set<number>>(new Set())

  const powerPointService = service as PowerPointService

  const handleGeneratePresentation = async () => {
    if (!topic.trim()) return

    setLoading(true)
    setError(null)
    
    try {
      // Generate presentation outline
      const generatedOutline = await aiService.generatePresentationOutline(
        topic,
        slideCount
      )
      
      // Enhance with additional details based on audience and tone
      const enhancedOutline = await Promise.all(
        generatedOutline.map(async (slide) => {
          const content = await aiService.generateSlideContent(
            `${topic} - ${slide.title}`,
            slide.slideType as any,
            {
              bulletPoints: slide.bulletPoints.length,
              tone
            }
          )
          
          return {
            ...slide,
            bulletPoints: content.content,
            notes: content.speakerNotes
          }
        })
      )
      
      setOutline(enhancedOutline)
      // Select all slides by default
      setSelectedSlides(new Set(enhancedOutline.map((_, i) => i)))
    } catch (err) {
      setError('Failed to generate presentation')
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  const handleCreatePresentation = async () => {
    if (outline.length === 0 || selectedSlides.size === 0) return

    setLoading(true)
    setError(null)
    
    try {
      // Get selected slides
      const slidesToCreate = outline.filter((_, index) => selectedSlides.has(index))
      
      // Create slides in PowerPoint
      for (const slide of slidesToCreate) {
        await Office.context.document.setSelectedDataAsync(
          slide.title,
          { coercionType: Office.CoercionType.Text },
          async (result) => {
            if (result.status === Office.AsyncResultStatus.Failed) {
              console.error('Failed to create slide:', result.error)
            }
          }
        )
        
        // Note: PowerPoint API is limited compared to Word/Excel
        // In a real implementation, we'd use the PowerPoint-specific API
        await powerPointService.insertSlide()
        
        // Add title
        await powerPointService.insertTextBox(slide.title, {
          fontSize: 44,
          bold: true,
          position: { x: 50, y: 50 }
        })
        
        // Add bullet points
        const bulletText = slide.bulletPoints.map(bp => `• ${bp}`).join('\n')
        await powerPointService.insertTextBox(bulletText, {
          fontSize: 24,
          position: { x: 50, y: 150 }
        })
        
        // Add speaker notes if available
        if (slide.notes) {
          // Speaker notes would be added through PowerPoint API
          console.log('Speaker notes:', slide.notes)
        }
      }
      
      alert(`Created ${slidesToCreate.length} slides successfully!`)
    } catch (err) {
      setError('Failed to create presentation')
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  const toggleSlideSelection = (index: number) => {
    const newSelection = new Set(selectedSlides)
    if (newSelection.has(index)) {
      newSelection.delete(index)
    } else {
      newSelection.add(index)
    }
    setSelectedSlides(newSelection)
  }

  const audienceOptions = [
    { value: 'general', label: 'General Audience' },
    { value: 'executive', label: 'Executive Team' },
    { value: 'technical', label: 'Technical Team' },
    { value: 'sales', label: 'Sales & Marketing' },
    { value: 'investors', label: 'Investors' },
    { value: 'academic', label: 'Academic' },
  ]

  const toneOptions = [
    { value: 'professional', label: 'Professional' },
    { value: 'casual', label: 'Casual' },
    { value: 'persuasive', label: 'Persuasive' },
    { value: 'informative', label: 'Informative' },
    { value: 'inspirational', label: 'Inspirational' },
  ]

  return (
    <div className="presentation-builder">
      <h3>AI Presentation Builder</h3>
      <p className="description">
        Generate a complete presentation outline with AI
      </p>

      <div className="builder-form">
        <div className="form-group">
          <label>Presentation Topic</label>
          <textarea
            placeholder="e.g., Quarterly Business Review, Product Launch, Training Session..."
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            className="topic-input"
            rows={3}
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Number of Slides</label>
            <input
              type="number"
              min="5"
              max="30"
              value={slideCount}
              onChange={(e) => setSlideCount(parseInt(e.target.value))}
              className="slide-count-input"
            />
          </div>

          <div className="form-group">
            <label>Target Audience</label>
            <select
              value={audience}
              onChange={(e) => setAudience(e.target.value)}
              className="audience-select"
            >
              {audienceOptions.map(opt => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Tone</label>
            <select
              value={tone}
              onChange={(e) => setTone(e.target.value)}
              className="tone-select"
            >
              {toneOptions.map(opt => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <button
          onClick={handleGeneratePresentation}
          disabled={loading || !topic.trim()}
          className="btn btn-primary"
        >
          {loading ? 'Generating...' : 'Generate Outline'}
        </button>
      </div>

      {error && (
        <div className="error-message">{error}</div>
      )}

      {loading && <LoadingSpinner message="Creating your presentation outline..." />}

      {outline.length > 0 && (
        <div className="presentation-outline">
          <div className="outline-header">
            <h4>Presentation Outline</h4>
            <span className="slide-count">
              {selectedSlides.size} of {outline.length} slides selected
            </span>
          </div>

          <div className="outline-slides">
            {outline.map((slide, index) => (
              <div 
                key={index} 
                className={`slide-preview ${selectedSlides.has(index) ? 'selected' : ''}`}
              >
                <div className="slide-header">
                  <input
                    type="checkbox"
                    checked={selectedSlides.has(index)}
                    onChange={() => toggleSlideSelection(index)}
                    className="slide-checkbox"
                  />
                  <span className="slide-number">Slide {slide.slideNumber}</span>
                  <span className={`slide-type slide-type-${slide.slideType}`}>
                    {slide.slideType}
                  </span>
                </div>
                
                <h5 className="slide-title">{slide.title}</h5>
                
                <ul className="slide-bullets">
                  {slide.bulletPoints.map((point, i) => (
                    <li key={i}>{point}</li>
                  ))}
                </ul>
                
                {slide.notes && (
                  <details className="speaker-notes">
                    <summary>Speaker Notes</summary>
                    <p>{slide.notes}</p>
                  </details>
                )}
              </div>
            ))}
          </div>

          <div className="outline-actions">
            <button
              onClick={handleCreatePresentation}
              disabled={loading || selectedSlides.size === 0}
              className="btn btn-primary"
            >
              Create Selected Slides
            </button>
            <button
              onClick={() => {
                setOutline([])
                setSelectedSlides(new Set())
              }}
              className="btn btn-secondary"
            >
              Clear Outline
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default PresentationBuilder